import { NavLink } from 'react-router-dom';
import { Grid2x2, Store, User } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface MobileBottomNavProps {
  signedIn: boolean;
  totalPacks: number;
}

/**
 * Fixed bottom tab bar for mobile (`<lg`). Three primary destinations:
 * Browse / Market / Account. The Account tab routes to `/sign-in` for
 * signed-out users and shows a pack-count badge for signed-in users.
 *
 * Hidden on `lg+` via the wrapping `<nav>`'s `lg:hidden` class.
 */
export function MobileBottomNav(props: MobileBottomNavProps): JSX.Element {
  const { signedIn, totalPacks } = props;
  const accountTo = signedIn ? '/account' : '/sign-in';

  return (
    <nav
      aria-label="Primary mobile"
      className="fixed bottom-0 left-0 right-0 z-30 h-14 bg-bg-1 border-t border-border lg:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="grid h-full grid-cols-3">
        <Tab to="/" testId="mobile-nav-browse" label="Browse" icon={<Grid2x2 className="size-5" />} />
        <Tab to="/marketplace" testId="mobile-nav-market" label="Market" icon={<Store className="size-5" />} />
        <Tab to={accountTo} testId="mobile-nav-account" label="Account" icon={<User className="size-5" />}>
          {signedIn && totalPacks > 0 ? (
            <span
              data-testid="mobile-nav-packs-badge"
              className="absolute -top-1 right-3 rounded-full bg-primary text-primary-foreground text-xs font-semibold px-1.5 py-0 leading-tight"
              aria-hidden="false"
            >
              {totalPacks}
            </span>
          ) : null}
        </Tab>
      </div>
    </nav>
  );
}

interface TabProps {
  to: string;
  testId: string;
  label: string;
  icon: React.ReactNode;
  children?: React.ReactNode;
}

function Tab(props: TabProps): JSX.Element {
  const { to, testId, label, icon, children } = props;
  return (
    <NavLink
      to={to}
      data-testid={testId}
      end={to === '/'}
      className={({ isActive }) =>
        cn(
          'relative flex flex-col items-center justify-center gap-0.5 no-underline min-h-11',
          isActive ? 'text-accent-primary' : 'text-text-1',
        )
      }
    >
      {icon}
      <span className="text-xs font-medium">{label}</span>
      {children}
    </NavLink>
  );
}
